/**
 * StepApprovalDetailPanel
 *
 * A full-width Dialog that shows approval details for a human_input step.
 * Two-column layout:
 *   Left  (220px) — list of approvals for this step (history across runs)
 *   Right (flex)  — ApprovalDetailPage content (embedded, no navigation)
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ReactMarkdown from "react-markdown";
import { listApprovals, getApprovalDetail, respondToApproval } from "../../api/agentApi";

// ─── Types (re-used from ApprovalDetailPage) ─────────────────────────────────

interface InputField {
  name: string;
  type: "text" | "textarea" | "select" | "boolean" | "number";
  label?: string;
  required?: boolean;
  default_value?: any;
  options?: string[];
  description?: string;
}

interface ApprovalDetail {
  id: string;
  workflow_run_id: string;
  step_id: string;
  title: string;
  prompt: string;
  display_data: any;
  input_fields: InputField[] | null;
  status: string;
  response_data: any;
  responded_by: string | null;
  responded_at: string | null;
  created_at: string | null;
  expires_at: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DECISION_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  approve:  { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0" },
  approved: { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0" },
  revise:   { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
  reject:   { bg: "#FEF2F2", color: "#991B1B", border: "#FECACA" },
  rejected: { bg: "#FEF2F2", color: "#991B1B", border: "#FECACA" },
};

// Modern Color Palette
const APPROVAL_PURPLE = "#8E588E";
const BG_SUBTLE = "#F8FAFC";    // Slate 50
const BORDER_COLOR = "#E2E8F0"; // Slate 200

const fmt = (iso?: string | null) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso!; }
};

const approvalStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "approved": return "success";
    case "rejected": return "error";
    case "pending":  return "warning";
    default:         return "default";
  }
};

// ─── Reusable sub-components ─────────────────────────────────────────────────

function ResponseDisplay({ data }: { data: any }) {
  if (!data || typeof data !== "object") {
    return <Typography sx={{ fontSize: "14px", color: "#334155" }}>{String(data ?? "—")}</Typography>;
  }
  const decision = data.approval ?? data.decision ?? data.action;
  const feedback = data.feedback ?? data.comments ?? data.reason ?? data.notes;
  const otherFields = Object.entries(data).filter(
    ([k]) => !["approval", "decision", "action", "feedback", "comments", "reason", "notes"].includes(k),
  );
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {decision && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600, minWidth: 70 }}>Decision</Typography>
          <Chip
            label={String(decision).charAt(0).toUpperCase() + String(decision).slice(1)}
            size="small"
            sx={{
              fontWeight: 700, fontSize: "13px",
              bgcolor: (DECISION_COLORS[String(decision).toLowerCase()] ?? DECISION_COLORS.approve).bg,
              color: (DECISION_COLORS[String(decision).toLowerCase()] ?? DECISION_COLORS.approve).color,
              border: `1px solid ${(DECISION_COLORS[String(decision).toLowerCase()] ?? DECISION_COLORS.approve).border}`,
            }}
          />
        </Box>
      )}
      {feedback && (
        <Box>
          <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600, mb: 0.5 }}>Feedback</Typography>
          <Typography sx={{ fontSize: "14px", lineHeight: 1.6, color: "#334155", pl: 0.5 }}>{String(feedback)}</Typography>
        </Box>
      )}
      {otherFields.map(([key, value]) => (
        <Box key={key}>
          <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600, mb: 0.5 }}>
            {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ")}
          </Typography>
          <Typography sx={{ fontSize: "14px", lineHeight: 1.6, color: "#334155", pl: 0.5 }}>
            {typeof value === "string" ? value : JSON.stringify(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function extractDisplayContent(displayData: any): { label: string; content: string }[] {
  if (!displayData || typeof displayData !== "object") return [];
  return Object.entries(displayData).map(([label, value]) => {
    let content: string;
    if (typeof value === "string") {
      content = value;
    } else if (typeof value === "object" && value !== null) {
      const inner = (value as any).output ?? (value as any).result ?? value;
      content = typeof inner === "string" ? inner : JSON.stringify(inner, null, 2);
    } else {
      content = String(value ?? "");
    }
    return { label, content };
  });
}

const markdownSx = {
  "& h1": { fontSize: "1.3rem", fontWeight: 700, mt: 1.5, mb: 0.75, color: "#1E293B" },
  "& h2": { fontSize: "1.1rem", fontWeight: 700, mt: 2, mb: 0.75, color: "#334155" },
  "& h3": { fontSize: "1rem", fontWeight: 600, mt: 1.5, mb: 0.5, color: "#475569" },
  "& p": { fontSize: "14px", lineHeight: 1.7, color: "#334155", mb: 1.25 },
  "& ul, & ol": { pl: 3, mb: 1.25 },
  "& li": { fontSize: "14px", lineHeight: 1.7, color: "#334155", mb: 0.5 },
  "& strong": { fontWeight: 700 },
  "& hr": { my: 1.5, borderColor: "#E2E8F0" },
  "& blockquote": { borderLeft: "3px solid #B87333", pl: 2, ml: 0, color: "#64748B" },
  "& code": { bgcolor: "#F1F5F9", px: 0.5, py: 0.25, borderRadius: "4px", fontSize: "13px" },
  "& pre": { bgcolor: "#F8FAFC", p: 2, borderRadius: "8px", overflow: "auto", fontSize: "13px" },
};

function ReviewContent({ approval }: { approval: ApprovalDetail }) {
  const displayEntries = useMemo(() => extractDisplayContent(approval.display_data), [approval.display_data]);
  const resolvedPrompt = useMemo(() => {
    if (!approval.prompt) return "";
    let text = approval.prompt;
    if (approval.display_data && typeof approval.display_data === "object") {
      for (const [label, value] of Object.entries(approval.display_data)) {
        const snakeKey = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        const content = typeof value === "string" ? value :
          typeof value === "object" && value !== null
            ? ((value as any).output ?? (value as any).result ?? JSON.stringify(value, null, 2))
            : String(value ?? "");
        text = text.replace(new RegExp(`\\{\\{\\s*${snakeKey}\\s*\\}\\}`, "gi"), content);
        text = text.replace(new RegExp(`\\{\\{\\s*${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\}\\}`, "gi"), content);
      }
    }
    return text;
  }, [approval.prompt, approval.display_data]);

  const hasPlaceholders = /\{\{[^}]+\}\}/.test(approval.prompt || "");
  const hasUnresolved = /\{\{[^}]+\}\}/.test(resolvedPrompt);
  const showUnified = hasPlaceholders && !hasUnresolved && displayEntries.length > 0;

  return (
    <>
      {showUnified ? (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: "1px solid #E2E8F0", borderRadius: "8px", bgcolor: "#FFFFFF" }}>
          <Box sx={markdownSx}><ReactMarkdown>{resolvedPrompt}</ReactMarkdown></Box>
        </Paper>
      ) : (
        <>
          {approval.prompt && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: "1px solid #E2E8F0", borderRadius: "8px", bgcolor: "#F8FAFC" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 1 }}>Instructions</Typography>
              <Typography sx={{ fontSize: "14px", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{approval.prompt}</Typography>
            </Paper>
          )}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: "1px solid #E2E8F0", borderRadius: "8px" }}>
            {displayEntries.length === 0 ? (
              <Typography sx={{ fontSize: "14px", color: "#94A3B8", fontStyle: "italic" }}>
                No upstream content available for review.
              </Typography>
            ) : (
              displayEntries.map(({ label, content }) => (
                <Box key={label} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 1 }}>{label}</Typography>
                  <Box sx={markdownSx}><ReactMarkdown>{content}</ReactMarkdown></Box>
                </Box>
              ))
            )}
          </Paper>
        </>
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface StepApprovalDetailPanelProps {
  open: boolean;
  onClose: () => void;
  /** The approval ID from the current workflow run's step */
  approvalId: string;
  stepId: string;
  stepLabel: string;
  /** Scope the history sidebar to a specific workflow run */
  workflowRunId?: string;
  workflowVersion?: number;
  workflowRunNumber?: number;
}

const StepApprovalDetailPanel: React.FC<StepApprovalDetailPanelProps> = ({
  open,
  onClose,
  approvalId,
  stepId,
  stepLabel,
  workflowRunId,
  workflowVersion,
  workflowRunNumber,
}) => {
  const [approvalList, setApprovalList]       = useState<any[]>([]);
  const [listLoading, setListLoading]         = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<ApprovalDetail | null>(null);
  const [detailLoading, setDetailLoading]     = useState(false);
  const [detailError, setDetailError]         = useState<string | null>(null);
  const [fieldValues, setFieldValues]         = useState<Record<string, any>>({});
  const [submitting, setSubmitting]           = useState(false);
  const [submitResult, setSubmitResult]       = useState<{ type: "success" | "error"; message: string } | null>(null);

  // ── fetch all approvals for this step ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setListLoading(true);
    listApprovals(null)
      .then((res: any) => {
        const all: any[] = Array.isArray(res) ? res : (res.approvals ?? []);
        // Scope to this workflow run first (if provided), then filter by step_id
        const scopedToRun = workflowRunId
          ? all.filter((a: any) => a.workflow_run_id === workflowRunId)
          : all;
        const filtered = scopedToRun.filter((a: any) => a.step_id === stepId);
        // If no matches by step_id within run, fall back to just the known one
        const list = filtered.length > 0 ? filtered : scopedToRun.filter((a: any) => a.id === approvalId);
        setApprovalList(list);
      })
      .catch(() => setApprovalList([]))
      .finally(() => setListLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepId, workflowRunId]);

  // ── load the pinned approvalId first ────────────────────────────────────
  const loadApproval = useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setSubmitResult(null);
    try {
      const data: ApprovalDetail = await getApprovalDetail(id);
      setSelectedApproval(data);
      const defaults: Record<string, any> = {};
      (data.input_fields ?? []).forEach((f: InputField) => {
        defaults[f.name] = f.default_value !== undefined ? f.default_value : f.type === "boolean" ? false : "";
      });
      setFieldValues(defaults);
    } catch (err) {
      setDetailError((err as Error).message);
      setSelectedApproval(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && approvalId) loadApproval(approvalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, approvalId]);

  const handleClose = () => {
    setApprovalList([]); setSelectedApproval(null); setFieldValues({}); setSubmitResult(null);
    onClose();
  };

  const handleFieldChange = (name: string, value: any) =>
    setFieldValues((prev) => ({ ...prev, [name]: value }));

  const isFormValid = () => {
    if (!selectedApproval?.input_fields) return true;
    return selectedApproval.input_fields.filter((f) => f.required).every((f) => {
      const val = fieldValues[f.name];
      return val !== undefined && val !== "" && val !== null;
    });
  };

  const handleFormSubmit = async () => {
    if (!selectedApproval) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const response = await respondToApproval(selectedApproval.id, fieldValues);
      const decision = response?.decision ?? fieldValues.decision;
      setSubmitResult({ type: "success", message: decision ? `Decision "${decision}" submitted successfully.` : "Response submitted." });
      await loadApproval(selectedApproval.id);
    } catch (err) {
      setSubmitResult({ type: "error", message: (err as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLegacySubmit = async (decision: "approve" | "reject") => {
    if (!selectedApproval) return;
    setSubmitting(true);
    setSubmitResult(null);
    try {
      await respondToApproval(selectedApproval.id, { decision, ...fieldValues });
      setSubmitResult({ type: "success", message: `${decision === "approve" ? "Approved" : "Rejected"} successfully.` });
      await loadApproval(selectedApproval.id);
    } catch (err) {
      setSubmitResult({ type: "error", message: (err as Error).message });
    } finally {
      setSubmitting(false);
    }
  };

  const hasFormFields = selectedApproval !== null && Array.isArray(selectedApproval.input_fields) && selectedApproval.input_fields.length > 0;
  const isExpired = selectedApproval?.expires_at && new Date(selectedApproval.expires_at) < new Date();
  const isPending = selectedApproval?.status === "pending" && !isExpired;

  // ── render ─────────────────────────────────────────────────────────────
  return (
<Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ 
        sx: { 
          height: "92vh", 
          borderRadius: "16px", 
          overflow: "hidden",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        } 
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          py: 2, px: 3,
          bgcolor: "white",
        }}
      >
        <Box sx={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'center', 
          width: 32, height: 32, borderRadius: '8px', bgcolor: `${APPROVAL_PURPLE}15` 
        }}>
          <PersonOutlineIcon sx={{ color: APPROVAL_PURPLE, fontSize: 20 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, fontSize: '1.1rem', color: '#1e293b' }}>
          {stepLabel} — Approval Details
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden", height: "100%", bgcolor: BG_SUBTLE }}>
        
        {/* ── Left: Approval History Sidebar ────────────────────────── */}
        <Box
          sx={{
            width: "22%",
            minWidth: 240,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: "white",
            borderRight: `1px solid ${BORDER_COLOR}`,
          }}
        >
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 800, color: "text.disabled", letterSpacing: 1.2 }}>
              Approval History
            </Typography>
            {(workflowVersion != null || workflowRunNumber != null) && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
                {workflowVersion != null && (
                  <Chip
                    label={`v${workflowVersion}`}
                    size="small"
                    sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: `${APPROVAL_PURPLE}12`, color: APPROVAL_PURPLE, border: `1px solid ${APPROVAL_PURPLE}30` }}
                  />
                )}
                {workflowRunNumber != null && (
                  <Chip
                    label={`Run #${workflowRunNumber}`}
                    size="small"
                    sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: "rgba(63,131,248,0.08)", color: "#1d4ed8", border: "1px solid rgba(63,131,248,0.25)" }}
                  />
                )}
              </Box>
            )}
          </Box>
          
          {listLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}><CircularProgress size={24} /></Box>
          ) : (
            <List
              sx={{
                flex: 1, overflowY: "auto", px: 1,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.05)", borderRadius: "10px" },
              }}
            >
              {approvalList.map((a) => {
                const isSelected = selectedApproval?.id === a.id;
                return (
                  <ListItem key={a.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => loadApproval(a.id)}
                      sx={{ 
                        py: 1.5, px: 2, borderRadius: "10px",
                        '&.Mui-selected': {
                          bgcolor: `${APPROVAL_PURPLE}10`,
                          borderLeft: `4px solid ${APPROVAL_PURPLE}`,
                          '&:hover': { bgcolor: `${APPROVAL_PURPLE}15` }
                        }
                      }}
                    >
                      <Stack spacing={0.5} sx={{ width: "100%" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 500, fontSize: "0.85rem", color: isSelected ? APPROVAL_PURPLE : '#475569' }}>
                            {a.title ?? "Approval"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                            {fmt(a.created_at)}
                          </Typography>
                          <Chip 
                            label={a.status ?? "unknown"} 
                            size="small" 
                            variant="outlined"
                            color={approvalStatusColor(a.status) as any}
                            sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, borderColor: BORDER_COLOR }} 
                          />
                        </Box>
                        {a.responded_by_username && a.status?.toLowerCase() !== "pending" && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                            <Typography variant="caption" sx={{ color: "#64748B", fontSize: "0.68rem" }}>
                              {a.status?.toLowerCase() === "approved" ? "Approved by:" :
                               a.status?.toLowerCase() === "rejected" ? "Rejected by:" :
                               a.status?.toLowerCase() === "revise"   ? "Revision by:" :
                               "Decided by:"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#1E293B", fontSize: "0.68rem", fontWeight: 600 }}>
                              {a.responded_by_username}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        {/* ── Right: Approval Detail Area ──────────────────────────── */}
        {/* Flex column: scrollable content + sticky decision footer */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Scrollable review area */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 4,
            "&::-webkit-scrollbar": { width: "9px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "#F1F5F9", borderRadius: "10px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E1", borderRadius: "10px", border: "2px solid #F1F5F9" },
            "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#94A3B8" },
          }}>
            {detailLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><CircularProgress size={32} /></Box>
            ) : detailError ? (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: '12px' }}>{detailError}</Alert>
            ) : selectedApproval ? (
              <Box sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}>

                {/* Status Header Card */}
                <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '12px', border: `1px solid ${BORDER_COLOR}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                      {selectedApproval.title || "Approval Required"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      ID: {selectedApproval.id} • Workflow: {selectedApproval.workflow_run_id}
                    </Typography>
                  </Box>
                  <Chip
                    label={isExpired ? "EXPIRED" : selectedApproval.status?.toUpperCase()}
                    color={isExpired ? "error" : selectedApproval.status === "pending" ? "warning" : selectedApproval.status === "approved" ? "success" : "error"}
                    sx={{ fontWeight: 800, px: 1 }}
                  />
                </Paper>

                {/* Review Content Card */}
                <Box sx={{ bgcolor: 'white', p: 4, borderRadius: '12px', border: `1px solid ${BORDER_COLOR}`, mb: 3 }}>
                  <Typography variant="overline" sx={{ fontWeight: 800, color: APPROVAL_PURPLE, mb: 2, display: 'block' }}>
                    Review Information
                  </Typography>
                  <ReviewContent approval={selectedApproval} />
                </Box>

                {/* Response record (non-pending state only) */}
                {!isPending && (
                  <Box sx={{ bgcolor: 'white', p: 4, borderRadius: '12px', border: `1px solid ${BORDER_COLOR}` }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: APPROVAL_PURPLE, mb: 2, display: 'block' }}>
                      Response Records
                    </Typography>
                    <ResponseDisplay data={selectedApproval.response_data} />
                    {selectedApproval.responded_at && (
                      <Typography variant="caption" sx={{ color: "text.disabled", mt: 3, display: "block", textAlign: 'right' }}>
                        Processed on {new Date(selectedApproval.responded_at).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Metadata Footer */}
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', px: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    Expires: {selectedApproval.expires_at ? new Date(selectedApproval.expires_at).toLocaleString() : "Never"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>
                    Created: {selectedApproval.created_at ? new Date(selectedApproval.created_at).toLocaleString() : "—"}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", color: 'text.disabled', height: "100%" }}>
                <Typography>Select an approval task to begin review</Typography>
              </Box>
          )}
          </Box>{/* end scrollable area */}

          {/* ── Sticky Decision Footer (pending approvals only) ───────── */}
          {isPending && selectedApproval && (
            <Box
              sx={{
                borderTop: `2px solid ${APPROVAL_PURPLE}30`,
                bgcolor: 'white',
                px: 4,
                py: 3,
                boxShadow: '0 -4px 16px rgba(142, 88, 142, 0.10)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Label row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: APPROVAL_PURPLE,
                  boxShadow: `0 0 0 3px ${APPROVAL_PURPLE}25`,
                }} />
                <Typography variant="overline" sx={{ fontWeight: 800, color: APPROVAL_PURPLE, letterSpacing: 1.2 }}>
                  Your Decision
                </Typography>
              </Box>

              {submitResult && (
                <Alert severity={submitResult.type} sx={{ borderRadius: '10px', py: 0.5 }} onClose={() => setSubmitResult(null)}>
                  {submitResult.message}
                </Alert>
              )}

              {hasFormFields ? (
                /* Form with properly typed fields */
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  {selectedApproval.input_fields!.map((field) => (
                    <Box key={field.name} sx={{ flex: field.type === 'textarea' ? '1 1 100%' : '1 1 200px', minWidth: 160 }}>
                      {field.type === 'textarea' ? (
                        <TextField
                          fullWidth multiline rows={2}
                          label={field.label || field.name}
                          value={fieldValues[field.name] ?? ''}
                          onChange={(e) => handleFieldChange(field.name, e.target.value)}
                          placeholder={field.description}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                      ) : field.type === 'select' ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>{field.label || field.name}</InputLabel>
                          <Select
                            value={fieldValues[field.name] ?? ''}
                            label={field.label || field.name}
                            onChange={(e) => handleFieldChange(field.name, e.target.value)}
                            sx={{ borderRadius: '10px' }}
                          >
                            {(field.options ?? []).map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : field.type === 'boolean' ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!fieldValues[field.name]}
                              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                            />
                          }
                          label={field.label || field.name}
                        />
                      ) : (
                        <TextField
                          fullWidth size="small"
                          label={field.label || field.name}
                          type={field.type === 'number' ? 'number' : 'text'}
                          value={fieldValues[field.name] ?? ''}
                          onChange={(e) => handleFieldChange(field.name, e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                      )}
                    </Box>
                  ))}
                  <Button
                    variant="contained" disableElevation
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={handleFormSubmit}
                    disabled={submitting || !isFormValid()}
                    sx={{
                      bgcolor: APPROVAL_PURPLE, '&:hover': { bgcolor: '#754875' },
                      borderRadius: '10px', px: 4, py: 1.1,
                      textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap',
                      alignSelf: 'flex-end',
                    }}
                  >
                    {submitting ? 'Processing…' : 'Submit Decision'}
                  </Button>
                </Box>
              ) : (
                /* Legacy: no input_fields — Approve / Reject buttons */
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined" color="error"
                    startIcon={<CancelOutlinedIcon />}
                    onClick={() => handleLegacySubmit('reject')}
                    disabled={submitting}
                    sx={{ borderRadius: '10px', px: 4, textTransform: 'none', fontWeight: 700 }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained" color="success" disableElevation
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => handleLegacySubmit('approve')}
                    disabled={submitting}
                    sx={{ borderRadius: '10px', px: 4, textTransform: 'none', fontWeight: 700 }}
                  >
                    {submitting ? 'Processing…' : 'Approve'}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>{/* end right column */}
      </DialogContent>
    </Dialog>
  );
};

export default StepApprovalDetailPanel;
